// theirs
import React from 'react';
import { Alert, CurrencyInput as DLSCurrencyInput, FieldControl, Hint, Label } from '@americanexpress/dls-react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';

// ours
import { CurrencyInputViewModel } from 'src/types/view-models/currency-input.view.model';
import InfoCallout from '../../callout/InfoCallout';

// styles
import styles from './CurrencyInput.module.scss';
import { useLocation } from 'react-router-dom';
import Selectors from 'src/utils/selectors';
import { trackClick } from 'src/hooks/useAdobeTags';

export default function CurrencyInput({
    id,
    label,
    hint,
    calloutText,
    showFractions,
    handleInputBlur,
    'data-cy': dataCy,
    className,
    hideLabel = false,
    tagName
}: CurrencyInputViewModel): JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers]: [FieldInputProps<number>, FieldMetaProps<number>, FieldHelperProps<number>] =
        useField(id);
    const hasError: boolean = meta.touched && meta.error?.length > 0;
    const { value }: { value: number } = meta;
    const {
        setValue,
        setTouched
    }: { setValue: (value: number) => void; setTouched: (value: boolean, shouldValidate?: boolean) => void } = helpers;
    const { pathname } = useLocation();
    // DLS requires we associate Alert with CurrencyInput in this particular way
    const alertId: string = `${id}Alert`;
    const describedBy: { describedBy?: string } = hasError ? { describedBy: alertId } : {};

    return (
        <FieldControl
            status={hasError ? 'error' : 'default'}
            data-cy={dataCy || `${id}CurrencyInput`}
            className={className}
        >
            {!hideLabel && <Label htmlFor={id}>{label}</Label>}
            {hint && <Hint htmlFor={id}>{hint}</Hint>}
            <DLSCurrencyInput
                theme={hideLabel ? { marginTop: 0 } : {}}
                id={id}
                showFractions={!!showFractions}
                // set value and onBlur manually so that we can sanitize the value
                value={value === 0 || value === undefined ? '' : value}
                onBlur={(e: any) => {
                    const inputValue = e.target.value.replace(/[,$]/g, '');
                    if (inputValue !== value?.toString() && tagName)
                        trackClick(pathname, `${tagName}>${e.target.value}`);
                    setTouched(true);
                    setValue(inputValue);
                    if (!isNaN(inputValue)) handleInputBlur?.(inputValue);
                }}
                // this handles the aria-label attribute
                // DLS tacks on 'USD $' to whatever we pass in here
                label={label}
                currency="USD"
                data-cy={Selectors.General.CURRENCY_INPUT}
                {...describedBy}
            />
            {hasError && (
                <Alert className={styles.alert} id={alertId} data-cy={`${id}${Selectors.General.CURRENCY_INPUT_ERROR}`}>
                    {meta.error}
                </Alert>
            )}
            {calloutText && <InfoCallout className={styles.currencyInputInfoCallout} text={calloutText} noMargin />}
        </FieldControl>
    );
}
